import { ReactNode } from "react";
import { twMerge } from "tailwind-merge";
import { CommandJob } from "~/components/build/Show/lib/types";
import PipelineStateIcon, {
  colorForJob,
} from "~/components/shared/PipelineStateIcon/PipelineStateIcon";
import Icon from "~/components/shared/Icon";
import { Badge } from "~/components/build/Show/routes/list/-components/Badge";
import { totalAnnotationCount } from "~/components/build/Show/components/AnnotationsBadge";
import { JobRetries } from "~/components/build/Show/routes/list/-components/TreeNode/JobRetries";
import { QueueLabel } from "~/components/build/Show/routes/list/-components/TreeNode/QueueLabel";
import { useBuild, useRetriesForJob } from "~/components/build/Show/lib/BuildContext";
import { getJobTimingCell } from "~/components/build/Show/routes/list/-components/timingColumns";
import { CommandStep, isDefaultCommandStep, isParallelCommandStep } from "~/lib/pipeline";
import { StateIcon } from "~/components/Playground/Node/BaseNode/StateIcon";
import { textColorForStep } from "~/components/Playground/Node/colors";
import { ParallelJobIndex } from "~/components/build/Show/components/ParallelJobIndex";
import { ParallelCommandJob } from "~/components/build/Show/lib/types/CommandJob";
import Emojify from "~/components/shared/Emojify";
import { useStepDependencies } from "~/components/build/Show/lib/useStepDependencies";
import { StepRow } from "~/components/build/Show/routes/list/-components/StepRow";

export const JobLabel = ({
  job,
  step,
  depth = 0,
  chevron,
  attempt,
}: {
  job: CommandJob;
  step: CommandStep;
  depth?: number;
  chevron?: ReactNode;
  /** When set, this row is a past retry attempt: badge it "Attempt #N" and
   * key the icon off the attempt's own state rather than the step's. */
  attempt?: number;
}) => {
  const { build } = useBuild();
  const retries = useRetriesForJob(job, { includeLatest: false });
  const { missingDependencies } = useStepDependencies(step.uuid);

  const waitingOnConcurrencyGroup = job.state === "limited";
  const waitingOnPlatformLimit = job.state === "platform_limited";
  const name = job.name || job.command || "";
  const timing = getJobTimingCell(job);
  // Attempt rows carry their own annotations; surface them like the step row
  // the history hangs off does (latest job/parallel rows are left unchanged).
  const annotationCount =
    attempt !== undefined ? totalAnnotationCount(job.statistics?.annotationCountsByStyle ?? {}) : 0;

  return (
    <StepRow
      depth={depth}
      chevron={chevron}
      stepType={step.type}
      icon={
        isDefaultCommandStep(step) && attempt === undefined ? (
          <StateIcon
            {...step}
            waitingOnConcurrencyGroup={waitingOnConcurrencyGroup}
            waitingOnPlatformLimit={waitingOnPlatformLimit}
            missingDependencies={missingDependencies}
            className={twMerge(
              textColorForStep({
                ...step,
                waitingOnConcurrencyGroup,
                waitingOnPlatformLimit,
                missingDependencies,
              }),
            )}
          />
        ) : (
          <PipelineStateIcon
            job={job}
            className={twMerge("w-4", colorForJob(job, build))}
            style={{ width: undefined, height: undefined }}
          />
        )
      }
      label={
        <span className="inline space-x-1">
          {isParallelCommandStep(step) && <ParallelJobIndex job={job as ParallelCommandJob} />}
          <Emojify text={name} />
        </span>
      }
      script={job.command || undefined}
      stepStats={
        attempt !== undefined ? (
          <Badge className="tabular-nums">Attempt #{attempt}</Badge>
        ) : retries.length > 0 ? (
          <JobRetries job={job} />
        ) : undefined
      }
      meta={
        <span className="inline-flex items-center gap-2">
          {annotationCount > 0 && (
            <Badge className="inline-flex items-center gap-1">
              <Icon icon="sticky-note" style={{ fill: "none" }} className="h-3 w-3 shrink-0" />
              {annotationCount}
              <span className="container-hidden @xl/list:inline">
                {annotationCount === 1 ? "annotation" : "annotations"}
              </span>
            </Badge>
          )}
          {job.clusterQueue ? (
            <QueueLabel
              queueKey={job.clusterQueue.key}
              os={job.instanceShape?.operatingSystem}
              job={job}
            />
          ) : null}
        </span>
      }
      timing={timing}
    />
  );
};
